import { model as router } from '!/router'
import { postpone } from '@/effector'
import { api } from '@setplex/tria-api'
import {
  attach,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { and } from 'patronum'
import { handleFavoriteBtnClick } from '~/features/favorites/model'
import { SignUpEntryPoint, SignUpMethod } from '~/shared/analytics/index.h'
import type { SendEventParams } from '~/shared/analytix'
import { socialAuthStarted } from '~/widgets/auth/model'
import * as signInModel from '~/widgets/auth/sign-in/model'
import * as signUpModel from '~/widgets/auth/sign-up/model'
import * as guestMyListModel from '~/widgets/favorites/guest/model'
import * as navbarNavigationModel from '~/widgets/navbar/navigation/model'
import { logErrorFx } from '../lib'
import { $readyAllInfo, afterSubscriberIdSettled } from '../session/model'
import { $analytix, $analytixEnabled } from '../wrapper'
import { type SocialSignUpMarker } from './index.h'
import {
  clearSocialSignUpMarker,
  readSocialSignUpMarker,
  writeSocialSignUpMarker,
} from './lib'

/** Backend marker for "the account this session belongs to is new". */
const IS_FIRST_LOGIN_PARAM = 'isFirstLogin'

const $signUpEventCanBeSent = and($readyAllInfo, $analytixEnabled)

export const sendSignUpFx = attach({
  source: $analytix,
  effect: (analytix, params: SendEventParams.SignUp) =>
    analytix.sendSignUpEvent(params),
})

sample({
  clock: sendSignUpFx.failData,
  fn: (e) => e,
  target: logErrorFx,
})

/**
 * Registration opens the session in the same response this event is clocked on, while
 * `content_user_subscriber_id` is only refreshed by the `/users/info` that follows it —
 * so without the hold the registration is reported against the guest id (OVP-3119).
 */
const signUpHappened = createEvent<SendEventParams.SignUp>()

sample({
  clock: afterSubscriberIdSettled(signUpHappened),
  target: sendSignUpFx,
})

/* Entry point — which surface opened the auth flow */

/**
 * The header control is both a real entry point and the fallback: it is the
 * app-wide way into the auth popup, and the surfaces the event dictionary does not
 * name (the favourite button on a content card, the home promo banner, a direct
 * link to `/sign-up`) open that same popup.
 *
 * Reset when a popup closes, on a successful email sign-in (that path never
 * fires `closeSignInPopup`), and on logout, so an abandoned flow cannot label
 * the next one. A successful sign up also closes the popup, but only off
 * `signUpModel.signUpFx`, the attach wrapper around `api.user.signUpFx` — and
 * the event below is clocked on the inner effect, which resolves first. A test
 * covers that ordering.
 */
export const $entryPoint = createStore<SignUpEntryPoint>(
  SignUpEntryPoint.Header
)

sample({
  clock: [navbarNavigationModel.signInClicked, handleFavoriteBtnClick],
  fn: () => SignUpEntryPoint.Header,
  target: $entryPoint,
})

sample({
  clock: [guestMyListModel.signUpClicked, guestMyListModel.signInClicked],
  target: $entryPoint,
})

sample({
  clock: [
    signInModel.closeSignInPopup,
    signUpModel.closeSignUpPopup,
    api.user.signInFx.done,
    api.user.logOutFx.done,
  ],
  fn: () => SignUpEntryPoint.Header,
  target: $entryPoint,
})

/* Email and password */

/**
 * A resolved `signUpFx` is account creation: since OVP-3045 the request creates the
 * account and opens the session in one call, so there is nothing further to wait for.
 *
 * The form is interactive before analytix is up. `postpone` reports that sign up
 * once it is, instead of dropping it. The entry point is taken here, not at send
 * time: a successful sign up closes the popup and resets `$entryPoint` before
 * analytix may be ready.
 */
const emailSignUpHappened = createEvent<SignUpEntryPoint>()

sample({
  clock: api.user.signUpFx.done,
  source: $entryPoint,
  target: emailSignUpHappened,
})

const reportEmailSignUp = createEvent<SignUpEntryPoint>()

postpone({
  clock: emailSignUpHappened,
  until: $signUpEventCanBeSent,
  target: reportEmailSignUp,
})

sample({
  clock: reportEmailSignUp,
  fn: (entryPoint) => ({
    signUpMethod: SignUpMethod.EmailPassword,
    entryPoint,
  }),
  target: signUpHappened,
})

/* Social providers */

const persistSocialSignUpMarkerFx = createEffect(
  (marker: SocialSignUpMarker) => {
    writeSocialSignUpMarker(marker)
  }
)

sample({
  clock: socialAuthStarted,
  source: $entryPoint,
  fn: (entryPoint, method) => ({ method, entryPoint }),
  target: persistSocialSignUpMarkerFx,
})

/**
 * Takes the marker and removes it in one step, so the event is sent once however
 * many times this runs. That matters: `?isFirstLogin` stays in the URL until the
 * region flow ends (see `routes/home/root/model/init`), so a reload or any further
 * navigation would otherwise look like another new account.
 */
const consumeSocialSignUpMarkerFx = createEffect(
  (): SocialSignUpMarker | null => {
    const marker = readSocialSignUpMarker()
    if (marker) clearSocialSignUpMarker()
    return marker
  }
)

/**
 * Read off the query rather than off the Home route that consumes the same param:
 * `redirectUrl` is the page the user left from, so a social sign up started
 * anywhere else comes back to that page and never reaches Home's `init`.
 */
sample({
  clock: [router.$query, $signUpEventCanBeSent],
  source: { query: router.$query, canSend: $signUpEventCanBeSent },
  filter: ({ query, canSend }) => canSend && query.has(IS_FIRST_LOGIN_PARAM),
  target: consumeSocialSignUpMarkerFx,
})

sample({
  clock: consumeSocialSignUpMarkerFx.doneData,
  filter: (marker): marker is SocialSignUpMarker => marker !== null,
  fn: ({ method, entryPoint }: SocialSignUpMarker) => ({
    signUpMethod: method,
    entryPoint,
  }),
  target: signUpHappened,
})

/**
 * A provider run that ends without a new account leaves the marker behind — the
 * user cancelled at the provider, or signed in to an account that already existed.
 * Those returns never hit `signInFx` / `signUpFx` (session comes back through
 * the cookie + `checkStatusFx`). Drop the marker on a navigation that is not a
 * first-login return, and also after email auth. Readiness is not a clock:
 * analytix becoming ready is not evidence the user left, and treating it as
 * such deletes the marker while the provider redirect is still in flight.
 */
const dropSocialSignUpMarkerFx = createEffect(() => {
  clearSocialSignUpMarker()
})

sample({
  clock: router.$location,
  source: router.$query,
  filter: (query) => !query.has(IS_FIRST_LOGIN_PARAM),
  target: dropSocialSignUpMarkerFx,
})

sample({
  clock: [api.user.signUpFx.done, api.user.signInFx.done],
  target: dropSocialSignUpMarkerFx,
})
